import { BRANDS, BrandId, DEFAULT_BRAND } from '@/constants/brands';

/**
 * Replace simple brand placeholders in a string.
 *
 * Supported placeholders:
 * - {{brand}}
 * - {{brand.name}}
 *
 * @param value String that may contain brand placeholders
 * @param brandId Brand to use for replacement
 */
export function replaceBrandPlaceholders(value: string, brandId: BrandId = DEFAULT_BRAND): string {
  if (typeof value !== 'string') {
    return value;
  }

  const brandName = BRANDS[brandId]?.name ?? BRANDS[DEFAULT_BRAND].name;
  const placeholderPattern = /{{\s*brand(?:\.name)?\s*}}/gi;

  return value.replace(placeholderPattern, brandName);
}

/**
 * Recursively replace brand placeholders for objects/arrays.
 * Useful for metadata definitions and other nested structures.
 */
export function replaceBrandPlaceholdersDeep<T>(input: T, brandId: BrandId = DEFAULT_BRAND): T {
  if (typeof input === 'string') {
    return replaceBrandPlaceholders(input, brandId) as unknown as T;
  }

  if (Array.isArray(input)) {
    return input.map(item => replaceBrandPlaceholdersDeep(item, brandId)) as unknown as T;
  }

  if (input && typeof input === 'object') {
    const entries = Object.entries(input as Record<string, unknown>).map(([key, value]) => [
      key,
      replaceBrandPlaceholdersDeep(value, brandId),
    ]);

    return Object.fromEntries(entries) as T;
  }

  return input;
}
